import { PaymentData } from '@/state/payment'
import { CC_ENTRY_MODES, PaymentMethodInputTypes } from '@/utils/constants'

/**
 * Returns the manual-keyed `input_mode` that is incompatible with the given
 * `cc_entry_mode`, or `null` when no cross-processor conflict is possible
 * (e.g. the venue is not a hosted-page venue).
 *
 * Background: both Fortis and Magensa Unigate are "hosted page" processors, so
 * merely checking `CC_ENTRY_MODES.includes(cc_entry_mode)` is not enough to
 * know whether a stored card can be charged at the current venue.  A card
 * tokenised by Magensa cannot be charged at a Fortis venue and vice-versa.
 */
export function getIncompatibleInputMode(ccEntryMode: string): PaymentMethodInputTypes | null {
  if (ccEntryMode === 'fortis_hosted_page') {
    return PaymentMethodInputTypes.MAGENSA_UNIGATE_ECOMMERCE_MANUAL_KEYED
  }
  if (ccEntryMode === 'magensa_unigate_hosted_page') {
    return PaymentMethodInputTypes.FORTIS_ECOMMERCE_MANUAL_KEYED
  }
  return null
}

/**
 * Filters out any stored payment methods whose `input_mode` cannot be charged
 * at a venue with the given `cc_entry_mode`.  Methods that are not
 * processor-specific manual-keyed cards (e.g. wallet tokens) are always kept.
 */
export function filterCompatiblePaymentMethods(methods: PaymentData[], ccEntryMode: string): PaymentData[] {
  const incompatibleMode = getIncompatibleInputMode(ccEntryMode)
  if (!incompatibleMode) return methods
  return methods.filter((pm) => pm.input_mode !== incompatibleMode)
}

/**
 * Returns `true` when `method` can be charged at a venue whose `cc_entry_mode`
 * is `ccEntryMode`.  Always returns `true` for venues that are not hosted-page
 * processors (no cross-processor conflict is possible).
 */
export function isPaymentMethodCompatibleWithVenue(method: PaymentData, ccEntryMode: string): boolean {
  const incompatibleMode = getIncompatibleInputMode(ccEntryMode)
  if (!incompatibleMode) return true
  return method.input_mode !== incompatibleMode
}

/**
 * Convenience: returns `true` when `ccEntryMode` is a known hosted-page mode
 * where cross-processor card conflicts can occur.
 */
export function isHostedPageVenue(ccEntryMode: string): boolean {
  return CC_ENTRY_MODES.includes(ccEntryMode)
}

/**
 * Returns `true` when `methods` contains at least one manually-keyed card token
 * (Fortis or Magensa).  Wallet tokens (Apple Pay / Google Pay) are excluded
 * because those are ephemeral — they are tracked via `payWithApplePayAtom` /
 * `payWithGooglePayAtom`, not as selectable stored cards.
 *
 * Use this after `filterCompatiblePaymentMethods` to decide whether a wallet
 * fallback is needed.
 */
export function hasCompatibleManualKeyedCard(methods: PaymentData[]): boolean {
  return methods.some(
    (pm) =>
      pm.input_mode === PaymentMethodInputTypes.FORTIS_ECOMMERCE_MANUAL_KEYED ||
      pm.input_mode === PaymentMethodInputTypes.MAGENSA_UNIGATE_ECOMMERCE_MANUAL_KEYED
  )
}
